// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Box,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconActivityHeartbeat,
  IconCalendarMonth,
  IconCheck,
  IconChevronDown,
  IconClipboardCheck,
  IconLayoutDashboard,
  IconLogout,
  IconMessageCircle,
  IconPill,
  IconSelector,
  IconSettings,
  IconSparkles,
  IconUsers,
  IconUsersGroup,
  IconHeartHandshake,
  IconBuildingHospital,
  IconFileText,
  IconCurrencyDollar,
  IconCalendarWeek
} from '@tabler/icons-react';
import type { Practitioner } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { getSiteSettings } from '../../utils/organizationSettings';
import { useLocation, useNavigate } from 'react-router';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  getPractitionerInitials,
  getPractitionerPhotoUrl,
  getOrganizationLogoUrl,
} from '../../utils/imageUtils';
import { clearSessionStorage } from '../../utils/secureLogout';
import { useMonitoringAccess } from '../../hooks/useMonitoringAccess';
import { AuthenticatedAvatar } from '../AuthenticatedAvatar';
import { hasDoseSpotIdentifier } from '../utils';
import classes from './Sidebar.module.css';

interface NavItemProps {
  icon: JSX.Element;
  label: string;
  href: string;
  /**
   * Open in a new browser tab rather than navigating in place.
   *
   * For surfaces a clinician keeps open alongside their work rather than
   * visiting: a monitoring queue is checked repeatedly through the day, and
   * replacing whatever chart they had open is the wrong default.
   */
  newTab?: boolean;
}

interface NavSection {
  title: string;
  items: NavItemProps[];
}

export function Sidebar(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const membership = medplum.getProjectMembership();
  const hasDoseSpot = hasDoseSpotIdentifier(membership);
  /*
   * Which monitoring surfaces this person may open. Navigation scoping only —
   * the routes gate too, and neither is a security boundary.
   */
  const access = useMonitoringAccess();
  const profile = medplum.getProfile();
  const {
    organizations,
    currentOrganization,
    requestOrganizationSwitch,
  } = useOrganization();

  const multiSiteEnabled = getSiteSettings(currentOrganization ?? undefined).multiSiteEnabled;

  // Use sidebar context for collapse/expand behavior
  const { mode, isExpanded, handleMouseEnter, handleMouseLeave } = useSidebar();
  // In hover mode, sidebar collapses when mouse leaves and expands when mouse enters
  const isCollapsed = mode === 'hover' && !isExpanded;

  const isActive = (href: string): boolean => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleNavClick = (href: string): void => {
    navigate(href);
  };

  const handleLogout = (): void => {
    // Clear all session and project-specific storage BEFORE signing out
    // This ensures no tokens or PHI remain after logout
    clearSessionStorage();
    // Sign out from Medplum (clears SDK tokens)
    medplum.signOut();
    // Redirect to signin page
    navigate('/signin');
  };

  // Build navigation sections - flat structure with section headers
  const allNavSections: NavSection[] = [
    {
      title: t('nav.sections.main', 'MAIN'),
      items: [
        { icon: <IconLayoutDashboard size={18} />, label: t('nav.dashboard'), href: '/practice' },
        { icon: <IconUsers size={18} />, label: t('nav.patients'), href: '/patients' },
        { icon: <IconCalendarMonth size={18} />, label: t('nav.calendar', 'Calendar'), href: '/appointments' },
        { icon: <IconCalendarWeek size={18} />, label: t('nav.schedule'), href: '/schedule' },
      ],
    },
    {
      title: t('nav.sections.tools', 'TOOLS'),
      items: [
        { icon: <IconSparkles size={18} />, label: t('nav.noukAi', 'Nouk AI'), href: '/spaces' },
        { icon: <IconMessageCircle size={18} />, label: t('nav.messages', 'Messages'), href: '/Message' },
        { icon: <IconClipboardCheck size={18} />, label: t('nav.tasks'), href: '/Task' },
        { icon: <IconSparkles size={18} />, label: t('nav.nlp', 'NLP'), href: '/ai-review' },
        ...(hasDoseSpot ? [{ icon: <IconPill size={18} />, label: t('nav.doseSpot'), href: '/integrations/dosespot' }] : []),
      ],
    },
    /*
     * Apps are the products that sit on top of the chart rather than inside
     * it: each has its own shell, its own worklists and its own vocabulary,
     * and a user is either licensed for one or does not see it.
     *
     * The distinction from TOOLS is not cosmetic. A tool acts on the patient
     * in front of you and belongs beside the chart; an app is a whole surface
     * somebody works in for a shift. Putting RPM under TOOLS sat it beside
     * Messages, which told a new nurse it was a feature rather than the place
     * their day happens. RCM and the rest land here as they ship.
     */
    {
      title: t('nav.sections.apps', 'APPS'),
      items: [
        /*
         * RPM opens in a new tab deliberately.
         *
         * It is a population worklist, not a chart view: a nurse keeps it open
         * all day alongside whatever chart they are working in. Navigating away
         * from it in the same tab is the wrong default, and the reason the
         * cross-tab session fix had to land first — before it, a queue left
         * idle in a second tab would sign the user out of the tab they were
         * actually using.
         */
        /*
         * Hidden rather than shown-and-blocked for someone without access. A
         * nav entry that always leads to a refusal teaches people to ignore the
         * nav; the route itself still gates, so a bookmarked link explains
         * itself rather than 404ing.
         *
         * While access is still loading the entry is shown. Treating "not
         * loaded yet" as "no access" would make the item flicker out on every
         * page load, which reads as a bug.
         */
        ...(access.loading || access.rpm
          ? [
              {
                icon: <IconActivityHeartbeat size={18} />,
                label: t('nav.rpm', 'RPM'),
                href: '/monitoring',
                newTab: true,
              },
            ]
          : []),
        /*
         * The Command Center lives inside the app, not here. It is one of the
         * app's own surfaces with the app's own shell, and listing it in the
         * provider app's sidebar put the same destination in two navs under
         * two names.
         */
      ],
    },
    {
      title: t('nav.sections.capture', 'CAPTURE'),
      items: [
        { icon: <IconHeartHandshake size={18} />, label: t('nav.sdoh', 'SDOH'), href: '/sdoh' },
        { icon: <IconFileText size={18} />, label: t('nav.forms', 'Forms'), href: '/form-library' },
        { icon: <IconCurrencyDollar size={18} />, label: t('nav.serviceList', 'Service list'), href: '/service-catalogue' },
        // Sites only make sense when the practice treats its locations as sites
        // within one clinical entity. Practices whose locations are separate
        // Organizations use the practice switcher instead, and showing both
        // would present two competing models of the same thing.
        ...(multiSiteEnabled
          ? [{ icon: <IconBuildingHospital size={18} />, label: t('nav.locations', 'Locations'), href: '/sites' }]
          : []),
      ],
    },
    {
      title: t('nav.sections.settings', 'SETTINGS'),
      items: [
        { icon: <IconUsersGroup size={18} />, label: t('nav.staff'), href: '/staff' },
        { icon: <IconSettings size={18} />, label: t('nav.settings'), href: '/settings' },
      ],
    },
  ];

  /*
   * A section whose every item was gated away renders as a bare heading over
   * nothing. APPS is the first section that can empty out — somebody without
   * an app licence has none of them — so the filter belongs here rather than
   * inside the renderer, where an empty section is already a layout decision
   * made too late.
   */
  const navSections = allNavSections.filter((section) => section.items.length > 0);

  const renderNavItem = (item: NavItemProps): JSX.Element => {
    // A new-tab destination is never "the page you are on", so it must not
    // render as active — that would leave the highlight stuck on it.
    const active = !item.newTab && isActive(item.href);

    const open = (): void => {
      if (item.newTab) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
        return;
      }
      handleNavClick(item.href);
    };

    // In collapsed mode, render only icons with tooltips
    if (isCollapsed) {
      return (
        <Tooltip key={item.label} label={item.label} position="right" withArrow>
          <UnstyledButton
            className={classes.navLinkCollapsed}
            data-active={active || undefined}
            onClick={open}
          >
            {item.icon}
          </UnstyledButton>
        </Tooltip>
      );
    }

    return (
      <NavLink
        key={item.label}
        label={item.label}
        leftSection={item.icon}
        active={active}
        onClick={open}
        className={classes.navLink}
      />
    );
  };

  const renderSection = (section: NavSection, index: number): JSX.Element => {
    return (
      <Box key={section.title}>
        {!isCollapsed && (
          <Text className={classes.sectionHeader}>
            {section.title}
          </Text>
        )}
        {isCollapsed && index > 0 && <Divider my="xs" />}
        {section.items.map((item) => renderNavItem(item))}
      </Box>
    );
  };

  const sidebarClassName = `${classes.sidebar} ${isCollapsed ? classes.sidebarCollapsed : ''}`;

  return (
    <Box
      className={classes.sidebarWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box className={sidebarClassName}>
        {/* Clinic/Practice Switcher */}
        <Box className={classes.clinicSwitcher}>
          {isCollapsed ? (
            <Tooltip label={currentOrganization?.name || t('nav.selectPractice')} position="right" withArrow>
              <Menu position="right-start" width={260} shadow="md">
                <Menu.Target>
                  <UnstyledButton className={classes.clinicSwitcherCollapsed}>
                    <AuthenticatedAvatar
                      size={32}
                      radius="md"
                      color="blue"
                      src={getOrganizationLogoUrl(currentOrganization)}
                    >
                      {currentOrganization?.name?.charAt(0) || 'P'}
                    </AuthenticatedAvatar>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{t('nav.switchPractice', 'Switch Practice')}</Menu.Label>
                  {organizations.map((org) => (
                    <Menu.Item
                      key={org.id}
                      onClick={() => requestOrganizationSwitch(org)}
                      leftSection={
                        <AuthenticatedAvatar size={24} radius="md" color="blue" src={getOrganizationLogoUrl(org)}>
                          {org.name?.charAt(0) || 'O'}
                        </AuthenticatedAvatar>
                      }
                      rightSection={org.id === currentOrganization?.id ? <IconCheck size={14} color="green" /> : null}
                    >
                      <Text size="sm">{org.name}</Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Tooltip>
          ) : (
            <Menu position="bottom-start" width={260} shadow="md">
              <Menu.Target>
                <UnstyledButton className={classes.clinicSwitcherButton}>
                  <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                    <AuthenticatedAvatar
                      size={32}
                      radius="md"
                      color="blue"
                      src={getOrganizationLogoUrl(currentOrganization)}
                    >
                      {currentOrganization?.name?.charAt(0) || 'P'}
                    </AuthenticatedAvatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {currentOrganization?.name || t('nav.selectPractice')}
                      </Text>
                      {currentOrganization?.address?.[0]?.city && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {currentOrganization.address[0].city}
                        </Text>
                      )}
                    </Box>
                  </Group>
                  <IconSelector size={16} className={classes.clinicChevron} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{t('nav.switchPractice', 'Switch Practice')}</Menu.Label>
                {organizations.map((org) => (
                  <Menu.Item
                    key={org.id}
                    onClick={() => requestOrganizationSwitch(org)}
                    leftSection={
                      <AuthenticatedAvatar size={24} radius="md" color="blue" src={getOrganizationLogoUrl(org)}>
                        {org.name?.charAt(0) || 'O'}
                      </AuthenticatedAvatar>
                    }
                    rightSection={org.id === currentOrganization?.id ? <IconCheck size={14} color="green" /> : null}
                  >
                    <Text size="sm">{org.name}</Text>
                    {org.address?.[0]?.city && (
                      <Text size="xs" c="dimmed">
                        {org.address[0].city}
                      </Text>
                    )}
                  </Menu.Item>
                ))}
                {organizations.length === 0 && (
                  <Menu.Item disabled>
                    <Text size="sm" c="dimmed">
                      {t('nav.noPracticesAvailable', 'No practices available')}
                    </Text>
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Box>

        <Divider />

        {/* Main Navigation with Sections */}
        <ScrollArea className={classes.navSection} type="scroll">
          <Box py="sm">
            {navSections.map((section, index) => renderSection(section, index))}
          </Box>
        </ScrollArea>

        {/* Footer with user info */}
        <Box className={classes.footer}>
          <Divider mb="sm" />
          <Group
            className={classes.footerGroup}
            justify={isCollapsed ? 'center' : 'space-between'}
            px={isCollapsed ? 'xs' : 'md'}
            py="xs"
          >
            <Group gap="sm">
              <AuthenticatedAvatar
                size={isCollapsed ? 32 : 36}
                radius="xl"
                color="blue"
                src={getPractitionerPhotoUrl(profile as Practitioner)}
              >
                {getPractitionerInitials(profile as Practitioner)}
              </AuthenticatedAvatar>
              {!isCollapsed && (
                <Box className={classes.userText}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {profile?.name?.[0]?.given?.[0] || 'User'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {profile?.name?.[0]?.family || ''}
                  </Text>
                </Box>
              )}
            </Group>
            {isCollapsed ? (
              <Tooltip label={t('nav.logout')} position="right" withArrow>
                <ActionIcon variant="subtle" color="red" onClick={handleLogout}>
                  <IconLogout size={18} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label={t('nav.logout')} position="top" withArrow>
                <ActionIcon variant="subtle" color="red" onClick={handleLogout}>
                  <IconLogout size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Box>
      </Box>
    </Box>
  );
}
